'use client';
import { authService } from '@/app/api/service/auth';
import { setXPartnerfromQueryString } from "@/app/utils/helper";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthFetched: boolean;
  autoLogin: (options?: { silent?: boolean }) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthFetched, setIsAuthFetched] = useState(false);
  const hasFetchedOnce = useRef(false);
  const requestId = useRef(0);

  const autoLogin = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    const currentRequest = ++requestId.current;

    try {
      if (!silent) {
        setIsLoading(true);
      }

      const authStatus = await authService.getAuthStatus();

      // Ignore out-of-order responses from overlapping navigations.
      if (currentRequest !== requestId.current) {
        return authStatus;
      }

      setIsAuthenticated(authStatus);
      return authStatus;
    } catch (error) {
      console.error(error);
      if (currentRequest === requestId.current) {
        setIsAuthenticated(false);
      }
      return false;
    } finally {
      if (currentRequest === requestId.current) {
        setIsLoading(false);
        setIsAuthFetched(true);
        hasFetchedOnce.current = true;
      }
    }
  }, []);

  // Fetch on mount and again on every client-side route change.
  // Login often soft-redirects via router.push, which would otherwise leave a
  // stale isAuthenticated=false until a hard refresh remounts the provider.
  useEffect(() => {
    setXPartnerfromQueryString();
    void autoLogin({ silent: hasFetchedOnce.current });
  }, [pathname, autoLogin]);

  return (
    <AuthContext.Provider value={{ isAuthFetched, isAuthenticated, isLoading, autoLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}